// Utilidades para la conexión con la base de datos
// RecomiendaT - Sistema de recomendaciones

import config from '../config';

const API_BASE_URL = config.API_BASE_URL;

class DatabaseService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Método genérico para hacer peticiones
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Agregar token de autenticación si existe
    const AUTH_TOKEN_KEY = 'authToken'; // Usar la clave directamente
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en la petición:', error);
      throw error;
    }
  }

  // Métodos de autenticación
  async login(email, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async register(userData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async logout() {
    return this.request('/auth/logout', {
      method: 'POST',
    });
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async updateProfile(profileData) {
    return this.request('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  // Métodos de usuarios
  async getUsers() {
    return this.request('/users');
  }

  async getUserById(id) {
    return this.request(`/users/${id}`);
  }

  async updateUser(id, userData) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  // Métodos de recomendaciones
  async getRecommendations(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/recommendations?${queryParams}`);
  }

  async getRecommendationById(id) {
    return this.request(`/recommendations/${id}`);
  }

  async createRecommendation(recommendationData) {
    return this.request('/recommendations', {
      method: 'POST',
      body: JSON.stringify(recommendationData),
    });
  }

  async updateRecommendation(id, recommendationData) {
    return this.request(`/recommendations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(recommendationData),
    });
  }

  async deleteRecommendation(id) {
    return this.request(`/recommendations/${id}`, {
      method: 'DELETE',
    });
  }

  // Métodos de categorías
  async getCategories() {
    return this.request('/categories');
  }

  async getCategoryById(id) {
    return this.request(`/categories/${id}`);
  }

  async createCategory(categoryData) {
    return this.request('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
  }

  async updateCategory(id, categoryData) {
    return this.request(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    });
  }

  async deleteCategory(id) {
    return this.request(`/categories/${id}`, {
      method: 'DELETE',
    });
  }

  // Métodos de likes y comentarios
  async toggleLike(recommendationId) {
    return this.request(`/recommendations/${recommendationId}/like`, {
      method: 'POST',
    });
  }

  async getLikes(recommendationId) {
    return this.request(`/recommendations/${recommendationId}/likes`);
  }

  async getComments(recommendationId) {
    return this.request(`/recommendations/${recommendationId}/comments`);
  }

  async createComment(recommendationId, commentData) {
    return this.request(`/recommendations/${recommendationId}/comments`, {
      method: 'POST',
      body: JSON.stringify(commentData),
    });
  }

  // Métodos de likes de comentarios
  async toggleCommentLike(commentId) {
    return this.request(`/comments/${commentId}/like`, {
      method: 'POST',
    });
  }

  async getCommentLikes(commentId) {
    return this.request(`/comments/${commentId}/likes`);
  }

  // Métodos de respuestas de comentarios
  async getCommentReplies(commentId) {
    return this.request(`/comments/${commentId}/replies`);
  }

  async createCommentReply(commentId, replyData) {
    return this.request(`/comments/${commentId}/replies`, {
      method: 'POST',
      body: JSON.stringify(replyData),
    });
  }

  // ===== Eventos (header) =====
  async getEvents() {
    return this.request(`/events`);
  }

  // Obtener evento por ID
  async getEventById(eventId) {
    return this.request(`/events/${eventId}`);
  }

  // Crear orden de boletos
  async createEventTicket(eventId, payload) {
    return this.request(`/events/${eventId}/tickets`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  // Métodos de utilidad
  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request('/upload', {
      method: 'POST',
      headers: {
        // No establecer Content-Type, dejar que el navegador lo maneje
      },
      body: formData,
    });
  }

  async search(query, type = 'all') {
    return this.request(`/search?q=${encodeURIComponent(query)}&type=${type}`);
  }
}

// Crear instancia única del servicio
const databaseService = new DatabaseService();

export default databaseService;
